/**
 * MONTICELLO SOAP COMPANY — THEME JAVASCRIPT
 * Wires up: sticky header, mobile nav (a11y), PDP tabs (a11y + keyboard),
 * FAQ accordion (a11y), variant picker (real variant matching),
 * quantity stepper, thumbnail gallery, sticky ATC, wishlist (persisted),
 * AJAX add-to-cart with live-region announcements, header cart count sync.
 */

(function () {
  'use strict';

  // ─── Helpers ────────────────────────────────────────────────────────────────
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function getJSON(selector) {
    var el = $(selector);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  // Inject a single visually-hidden live region for announcements.
  function ensureLiveRegion() {
    var live = $('#a11y-live-region');
    if (live) return live;
    live = document.createElement('div');
    live.id = 'a11y-live-region';
    live.className = 'visually-hidden';
    live.setAttribute('aria-live', 'polite');
    live.setAttribute('aria-atomic', 'true');
    document.body.appendChild(live);
    return live;
  }
  function announce(msg) {
    var live = ensureLiveRegion();
    // Clear first so the same message re-announces.
    live.textContent = '';
    setTimeout(function () { live.textContent = msg; }, 50);
  }

  function formatMoney(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: currency || 'USD'
      }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  // ─── Sticky header shadow on scroll ─────────────────────────────────────────
  function initStickyHeader() {
    var header = $('.site-header');
    if (!header) return;
    var ticking = false;
    window.addEventListener('scroll', function () {
      if (!ticking) {
        window.requestAnimationFrame(function () {
          header.classList.toggle('scrolled', window.scrollY > 60);
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  // ─── Mobile nav with full a11y ──────────────────────────────────────────────
  function initMobileNav() {
    var toggle = $('.mobile-menu-toggle');
    var nav = $('.mobile-nav');
    var closeBtn = $('.mobile-nav__close-btn');
    if (!toggle || !nav) return;

    var lastFocused = null;

    function getFocusable() {
      return $$('a, button, input, [tabindex]:not([tabindex="-1"])', nav)
        .filter(function (el) { return !el.hasAttribute('disabled') && el.offsetParent !== null; });
    }

    function open() {
      lastFocused = document.activeElement;
      nav.classList.add('is-open');
      nav.removeAttribute('aria-hidden');
      toggle.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      var first = getFocusable()[0];
      if (first) first.focus();
    }

    function close() {
      nav.classList.remove('is-open');
      nav.setAttribute('aria-hidden', 'true');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      if (lastFocused && lastFocused.focus) lastFocused.focus();
    }

    toggle.addEventListener('click', open);
    if (closeBtn) closeBtn.addEventListener('click', close);

    // Escape closes; Tab is trapped while open
    document.addEventListener('keydown', function (e) {
      if (!nav.classList.contains('is-open')) return;
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key === 'Tab') {
        var f = getFocusable();
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });
  }

  // ─── PDP: Thumbnail gallery ─────────────────────────────────────────────────
  function initGallery() {
    var thumbs = $$('.pdp__thumb');
    var main = $('#pdp-main-img');
    if (!thumbs.length || !main) return;

    thumbs.forEach(function (thumb, idx) {
      // Make thumbs keyboard-focusable buttons.
      thumb.setAttribute('tabindex', '0');
      thumb.setAttribute('role', 'button');
      var imgEl = $('img', thumb);
      if (imgEl) thumb.setAttribute('aria-label', 'View image ' + (idx + 1));

      function activate() {
        thumbs.forEach(function (t) { t.classList.remove('active'); });
        thumb.classList.add('active');
        var src = thumb.getAttribute('data-src') || (imgEl && imgEl.src);
        if (src) main.src = src;
      }
      thumb.addEventListener('click', activate);
      thumb.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activate(); }
      });
    });
  }

  // ─── PDP: Variant picker (real variant matching) ────────────────────────────
  function initVariantPicker() {
    var optionBtns = $$('.pdp__scent-btn');
    if (!optionBtns.length) return;

    var variants = getJSON('[data-product-variants]');
    if (!variants) {
      // Fallback: visual-only selection without real variant switching.
      optionBtns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var pos = btn.getAttribute('data-option-position');
          $$('.pdp__scent-btn[data-option-position="' + pos + '"]').forEach(function (b) {
            b.classList.remove('active');
            b.setAttribute('aria-pressed', 'false');
          });
          btn.classList.add('active');
          btn.setAttribute('aria-pressed', 'true');
        });
      });
      return;
    }

    // Track selected option values keyed by 1-based position.
    var selected = {};
    optionBtns.forEach(function (btn) {
      if (btn.classList.contains('active')) {
        selected[btn.getAttribute('data-option-position')] = btn.getAttribute('data-value');
      }
      // Set initial aria-pressed.
      btn.setAttribute('aria-pressed', btn.classList.contains('active') ? 'true' : 'false');
    });

    var idInput = $('#variant-id');
    var priceEl = $('#pdp-price');
    var compareEl = $('.pdp__compare-price');
    var saveBadge = $('.pdp__save-badge');
    var atcPriceEl = $('.atc-price');
    var atcBtn = $('.pdp__atc-btn');
    var buyBtn = $('.pdp__buy-now');
    var stickyVariant = $('#sticky-variant');

    function findMatchingVariant() {
      return variants.find(function (v) {
        return v.options.every(function (val, i) {
          return selected[String(i + 1)] === val;
        });
      });
    }

    function updateForVariant(variant) {
      if (!variant) return;
      if (idInput) idInput.value = variant.id;
      if (priceEl) priceEl.textContent = variant.price;
      if (atcPriceEl) atcPriceEl.textContent = variant.price;
      if (compareEl) {
        if (variant.compare_at_price) {
          compareEl.textContent = variant.compare_at_price;
          compareEl.style.display = '';
          if (saveBadge) saveBadge.style.display = '';
        } else {
          compareEl.style.display = 'none';
          if (saveBadge) saveBadge.style.display = 'none';
        }
      }
      // Availability state
      var unavailable = !variant.available;
      if (atcBtn) {
        atcBtn.disabled = unavailable;
        if (unavailable) {
          atcBtn.dataset.originalLabel = atcBtn.dataset.originalLabel || atcBtn.innerHTML;
          atcBtn.textContent = 'Sold Out';
        } else if (atcBtn.dataset.originalLabel) {
          atcBtn.innerHTML = atcBtn.dataset.originalLabel;
        }
      }
      if (buyBtn) buyBtn.disabled = unavailable;
      if (stickyVariant) {
        var labels = Object.keys(selected).sort().map(function (k) { return selected[k]; });
        stickyVariant.textContent = labels.join(' / ');
      }
      // Update visible option labels
      optionBtns.forEach(function (btn) {
        if (btn.classList.contains('active')) {
          var labelEl = btn.closest('div[style*="margin-bottom"]').querySelector('.pdp__option-label span');
          if (labelEl) labelEl.textContent = btn.getAttribute('data-value');
        }
      });
    }

    optionBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var pos = btn.getAttribute('data-option-position');
        var val = btn.getAttribute('data-value');
        $$('.pdp__scent-btn[data-option-position="' + pos + '"]').forEach(function (b) {
          b.classList.remove('active');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('active');
        btn.setAttribute('aria-pressed', 'true');
        selected[pos] = val;
        var v = findMatchingVariant();
        updateForVariant(v);
      });
    });

    // Initial label sync
    var initial = findMatchingVariant();
    if (initial && atcBtn) atcBtn.dataset.originalLabel = atcBtn.innerHTML;
  }

  // ─── PDP: Quantity stepper (syncs to hidden input) ──────────────────────────
  function initQuantityStepper() {
    var minus = $('.pdp__qty-btn--minus');
    var plus = $('.pdp__qty-btn--plus');
    var valEl = $('.pdp__qty-val');
    var hidden = $('#qty-input');
    if (!minus || !plus || !valEl) return;

    function set(n) {
      n = Math.max(1, n);
      valEl.textContent = n;
      if (hidden) hidden.value = n;
    }
    minus.addEventListener('click', function () { set(parseInt(valEl.textContent, 10) - 1); });
    plus.addEventListener('click', function () { set(parseInt(valEl.textContent, 10) + 1); });
    set(parseInt(valEl.textContent, 10) || 1);
  }

  // ─── PDP: Tabs (a11y + keyboard nav) ────────────────────────────────────────
  function initTabs() {
    var tabs = $$('.pdp__tab-btn');
    if (!tabs.length) return;

    function activate(idx) {
      tabs.forEach(function (t, i) {
        var on = i === idx;
        t.classList.toggle('active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.setAttribute('tabindex', on ? '0' : '-1');
        var panelId = t.getAttribute('aria-controls');
        var panel = panelId ? document.getElementById(panelId) : null;
        if (panel) {
          panel.classList.toggle('active', on);
          if (on) panel.removeAttribute('hidden');
          else panel.setAttribute('hidden', '');
        }
      });
      tabs[idx].focus();
    }

    tabs.forEach(function (tab, idx) {
      tab.addEventListener('click', function () { activate(idx); });
      tab.addEventListener('keydown', function (e) {
        var next = null;
        if (e.key === 'ArrowRight') next = (idx + 1) % tabs.length;
        else if (e.key === 'ArrowLeft') next = (idx - 1 + tabs.length) % tabs.length;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = tabs.length - 1;
        if (next !== null) { e.preventDefault(); activate(next); }
      });
    });
  }

  // ─── PDP: FAQ accordion (a11y) ──────────────────────────────────────────────
  function initFaq() {
    var items = $$('.pdp__faq-item');
    items.forEach(function (item) {
      var btn = $('.pdp__faq-btn', item);
      var answerId = btn && btn.getAttribute('aria-controls');
      var answer = answerId ? document.getElementById(answerId) : $('.pdp__faq-answer', item);
      if (!btn || !answer) return;

      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        item.classList.toggle('open', !open);
        if (open) answer.setAttribute('hidden', '');
        else answer.removeAttribute('hidden');
      });
    });
  }

  // ─── PDP: Sticky mobile ATC visibility ──────────────────────────────────────
  function initStickyAtc() {
    var sticky = $('.sticky-atc');
    var addRow = $('.pdp__add-row');
    if (!sticky || !addRow || !('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        sticky.classList.toggle('visible', !entry.isIntersecting);
      });
    }, { threshold: 0 });
    observer.observe(addRow);
  }

  // ─── Wishlist with localStorage persistence ─────────────────────────────────
  function initWishlist() {
    var btn = $('.pdp__wishlist-btn');
    if (!btn) return;

    // Derive a stable key for this product.
    var productKey = (document.body.className.match(/template-product/) ? location.pathname : null);
    var storage = null;
    try { storage = window.localStorage; } catch (e) { storage = null; }

    function getList() {
      if (!storage) return [];
      try { return JSON.parse(storage.getItem('msc_wishlist') || '[]'); } catch (e) { return []; }
    }
    function setList(arr) {
      if (!storage) return;
      try { storage.setItem('msc_wishlist', JSON.stringify(arr)); } catch (e) {}
    }
    function render(active) {
      btn.classList.toggle('active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      var icon = $('svg', btn);
      if (icon) {
        icon.style.fill = active ? '#e53935' : 'none';
        icon.style.color = active ? '#e53935' : '#121212';
      }
    }

    var list = getList();
    var isOn = productKey && list.indexOf(productKey) !== -1;
    btn.setAttribute('aria-pressed', isOn ? 'true' : 'false');
    render(isOn);

    btn.addEventListener('click', function () {
      if (!productKey) { render(!btn.classList.contains('active')); return; }
      var current = getList();
      var i = current.indexOf(productKey);
      if (i === -1) { current.push(productKey); render(true); announce('Added to wishlist'); }
      else { current.splice(i, 1); render(false); announce('Removed from wishlist'); }
      setList(current);
    });
  }

  // ─── Cart count sync (Shopify AJAX) ─────────────────────────────────────────
  function updateCartCount() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        $$('.cart-count').forEach(function (el) {
          el.textContent = cart.item_count;
          el.style.display = cart.item_count > 0 ? 'flex' : 'none';
          el.setAttribute('aria-label', cart.item_count + ' items in cart');
          if (cart.item_count > 0) el.removeAttribute('aria-hidden');
        });
        return cart;
      })
      .catch(function () {});
  }

  // ─── Add-to-cart AJAX ───────────────────────────────────────────────────────
  function initAtcForms() {
    $$('[data-atc-form]').forEach(function (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var idEl = form.querySelector('[name="id"]');
        var qtyEl = form.querySelector('[name="quantity"]');
        if (!idEl) return;
        var variantId = idEl.value;
        var qty = qtyEl ? Math.max(1, parseInt(qtyEl.value, 10) || 1) : 1;
        var btn = $('[data-atc-btn]', form);
        var originalHTML = btn ? btn.innerHTML : null;

        if (btn) {
          btn.disabled = true;
          btn.setAttribute('aria-busy', 'true');
          btn.textContent = 'Adding…';
        }

        fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: qty })
        })
          .then(function (r) {
            if (!r.ok) throw new Error('add failed');
            return r.json();
          })
          .then(function (line) {
            announce((line && line.title ? line.title : 'Item') + ' added to your cart');
            return updateCartCount();
          })
          .then(function () {
            if (btn) {
              btn.textContent = '✓ Added';
              setTimeout(function () {
                if (originalHTML) btn.innerHTML = originalHTML;
                btn.disabled = false;
                btn.removeAttribute('aria-busy');
              }, 1800);
            }
          })
          .catch(function () {
            announce('Could not add to cart. Please try again.');
            if (btn) {
              btn.textContent = 'Try again';
              setTimeout(function () {
                if (originalHTML) btn.innerHTML = originalHTML;
                btn.disabled = false;
                btn.removeAttribute('aria-busy');
              }, 1800);
            }
          });
      });
    });
  }

  // ─── Email-capture form (let Shopify submit; just give feedback) ────────────
  function initEmailForms() {
    $$('[data-email-form]').forEach(function (form) {
      form.addEventListener('submit', function () {
        var btn = $('[type="submit"]', form);
        if (btn) {
          btn.setAttribute('aria-busy', 'true');
          btn.disabled = true;
        }
      });
    });
  }

  // ─── Boot ───────────────────────────────────────────────────────────────────
  function boot() {
    ensureLiveRegion();
    initStickyHeader();
    initMobileNav();
    initGallery();
    initVariantPicker();
    initQuantityStepper();
    initTabs();
    initFaq();
    initStickyAtc();
    initWishlist();
    initAtcForms();
    initEmailForms();
    updateCartCount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
